// Minimal QR Code generator — byte mode, ECC level M, versions 1–10.
//
// Zero-dependency inline implementation so the pairing screen can render a
// scannable QR without pulling an npm package (docs/01: every dependency is a
// risk on weak hardware). Algorithm adapted from Project Nayuki's "QR Code
// generator" reference (MIT License) — trimmed to the single mode + ECC level +
// version range the pair link needs (a ~50-char URL fits comfortably in v4-M).
//
// Output: `encodeQr(text)` → boolean[][] (true = dark module), plus
// `qrToSvg(matrix)` → a self-contained <svg> string for crisp rendering.

// --- Error-correction tables (ECC level M), versions 0..10 ----------------------
// Index [version]; index 0 is an illegal placeholder to keep versions 1-based.
const ECC_CODEWORDS_PER_BLOCK = [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26];
const NUM_EC_BLOCKS = [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5];
const MIN_VERSION = 1;
const MAX_VERSION = 10;

// --- GF(256) tables for Reed-Solomon (primitive polynomial 0x11D) ---------------
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function initGf() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;
}

/** Reed-Solomon ECC codewords for `data` at the given degree. */
function rsCompute(data: Uint8Array, degree: number): Uint8Array {
  // Generator polynomial.
  const gen = new Uint8Array(degree);
  gen[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      gen[j] = gfMul(gen[j]!, root);
      if (j + 1 < degree) gen[j]! ^= gen[j + 1]!;
    }
    root = gfMul(root, 2);
  }
  const result = new Uint8Array(degree);
  for (const b of data) {
    const factor = b ^ result[0]!;
    result.copyWithin(0, 1);
    result[degree - 1] = 0;
    for (let j = 0; j < degree; j++) result[j]! ^= gfMul(gen[j]!, factor);
  }
  return result;
}

// --- Bit buffer ----------------------------------------------------------------
class BitBuf {
  bits: number[] = [];
  append(val: number, len: number): void {
    for (let i = len - 1; i >= 0; i--) this.bits.push((val >>> i) & 1);
  }
}

// --- Version / capacity --------------------------------------------------------
function numRawDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
}

function numDataCodewords(ver: number): number {
  return (
    Math.floor(numRawDataModules(ver) / 8) - ECC_CODEWORDS_PER_BLOCK[ver]! * NUM_EC_BLOCKS[ver]!
  );
}

function pickVersion(dataLen: number): number {
  for (let ver = MIN_VERSION; ver <= MAX_VERSION; ver++) {
    // Byte mode: 4-bit mode indicator + char-count indicator + payload bytes.
    const ccBits = ver < 10 ? 8 : 16;
    const capacityBits = numDataCodewords(ver) * 8;
    if (4 + ccBits + dataLen * 8 <= capacityBits) return ver;
  }
  throw new Error(`QR: ${dataLen} bytes exceeds v${MAX_VERSION}-M capacity`);
}

function alignmentPositions(ver: number): number[] {
  if (ver === 1) return [];
  const size = ver * 4 + 17;
  const numAlign = Math.floor(ver / 7) + 2;
  const step = Math.ceil((size - 13) / (numAlign * 2 - 2)) * 2;
  const result: number[] = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

// --- Matrix helpers ------------------------------------------------------------
type Grid = boolean[][];

function makeGrid(size: number): Grid {
  return Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
}

function drawFinder(grid: Grid, fn: Grid, x: number, y: number, size: number): void {
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const xx = x + dx;
      const yy = y + dy;
      if (xx < 0 || xx >= size || yy < 0 || yy >= size) continue;
      const dist = Math.max(Math.abs(dx - 3), Math.abs(dy - 3));
      grid[yy]![xx] = dist !== 2 && dist !== 4;
      fn[yy]![xx] = true;
    }
  }
}

function encodeData(text: string, ver: number): Uint8Array {
  const bytes = new TextEncoder().encode(text);
  const bb = new BitBuf();
  bb.append(0b0100, 4); // byte mode
  bb.append(bytes.length, ver < 10 ? 8 : 16);
  for (const b of bytes) bb.append(b, 8);

  const capacityBits = numDataCodewords(ver) * 8;
  // Terminator (up to 4 bits) + pad to a byte boundary.
  bb.append(0, Math.min(4, capacityBits - bb.bits.length));
  while (bb.bits.length % 8 !== 0) bb.bits.push(0);

  // Pack into codewords, then alternate pad bytes.
  const dataCodewords = new Uint8Array(numDataCodewords(ver));
  for (let i = 0; i < bb.bits.length; i++) {
    if (bb.bits[i]) dataCodewords[i >>> 3]! |= 0x80 >>> (i & 7);
  }
  for (let i = Math.ceil(bb.bits.length / 8), pad = 0xec; i < dataCodewords.length; i++, pad ^= 0xec ^ 0x11) {
    dataCodewords[i] = pad;
  }
  return dataCodewords;
}

/** Interleave data + ECC codewords across blocks per the QR spec. */
function interleave(dataCodewords: Uint8Array, ver: number): Uint8Array {
  const numBlocks = NUM_EC_BLOCKS[ver]!;
  const eccLen = ECC_CODEWORDS_PER_BLOCK[ver]!;
  const rawCodewords = Math.floor(numRawDataModules(ver) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const blocks: Uint8Array[] = [];
  const ecc: Uint8Array[] = [];
  let off = 0;
  for (let i = 0; i < numBlocks; i++) {
    const dataLen = shortBlockLen - eccLen + (i < numShortBlocks ? 0 : 1);
    const dat = dataCodewords.slice(off, off + dataLen);
    off += dataLen;
    blocks.push(dat);
    ecc.push(rsCompute(dat, eccLen));
  }

  const result: number[] = [];
  const maxData = shortBlockLen - eccLen + 1;
  for (let i = 0; i < maxData; i++) {
    for (let b = 0; b < numBlocks; b++) {
      // Short blocks skip their (absent) last data codeword.
      if (i < blocks[b]!.length) result.push(blocks[b]![i]!);
    }
  }
  for (let i = 0; i < eccLen; i++) {
    for (let b = 0; b < numBlocks; b++) result.push(ecc[b]![i]!);
  }
  return Uint8Array.from(result);
}

function drawFunctionPatterns(grid: Grid, fn: Grid, ver: number): void {
  const size = grid.length;
  // Timing patterns.
  for (let i = 0; i < size; i++) {
    grid[6]![i] = i % 2 === 0;
    grid[i]![6] = i % 2 === 0;
    fn[6]![i] = true;
    fn[i]![6] = true;
  }
  // Finder patterns + separators.
  drawFinder(grid, fn, 0, 0, size);
  drawFinder(grid, fn, size - 7, 0, size);
  drawFinder(grid, fn, 0, size - 7, size);
  // Alignment patterns.
  const positions = alignmentPositions(ver);
  for (const ay of positions) {
    for (const ax of positions) {
      // Skip the three that collide with finder patterns.
      if ((ax === 6 && ay === 6) || (ax === 6 && ay === size - 7) || (ax === size - 7 && ay === 6)) {
        continue;
      }
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          grid[ay + dy]![ax + dx] = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
          fn[ay + dy]![ax + dx] = true;
        }
      }
    }
  }
  // Reserve the format-info areas as function modules (values set later). These
  // ranges must match drawFormatBits EXACTLY — over-reserving even one cell would
  // make drawCodewords skip a data module and shift the whole data region.
  // First copy: row 8 cols 0..8 and col 8 rows 0..8 (9 each).
  for (let i = 0; i < 9; i++) {
    fn[8]![i] = true;
    fn[i]![8] = true;
  }
  // Second copy: row 8 cols size-8..size-1 and col 8 rows size-8..size-1 (8 each;
  // the col-8 range includes the dark module at row size-8).
  for (let i = 0; i < 8; i++) {
    fn[8]![size - 1 - i] = true;
    fn[size - 1 - i]![8] = true;
  }
  grid[size - 8]![8] = true; // dark module
  if (ver >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        fn[size - 11 + j]![i] = true;
        fn[i]![size - 11 + j] = true;
      }
    }
  }
}

function drawCodewords(grid: Grid, fn: Grid, data: Uint8Array): void {
  const size = grid.length;
  let bit = 0;
  const total = data.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // skip the vertical timing column
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (fn[y]![x]) continue;
        if (bit < total) {
          grid[y]![x] = ((data[bit >>> 3]! >>> (7 - (bit & 7))) & 1) !== 0;
          bit++;
        }
      }
    }
  }
}

function applyMask(grid: Grid, fn: Grid, mask: number): void {
  const size = grid.length;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (fn[y]![x]) continue;
      let invert: boolean;
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break;
        case 1: invert = y % 2 === 0; break;
        case 2: invert = x % 3 === 0; break;
        case 3: invert = (x + y) % 3 === 0; break;
        case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
      }
      if (invert) grid[y]![x] = !grid[y]![x];
    }
  }
}

function drawFormatBits(grid: Grid, ver: number, mask: number): void {
  const size = grid.length;
  // ECC level M = 0b00; combine with mask, then BCH(15,5) + XOR mask.
  const data = (0b00 << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;

  const get = (i: number) => ((bits >>> i) & 1) !== 0;
  // First copy, around the top-left finder. grid is [row][col]; the format
  // strip runs down column 8 and along row 8 (skipping the timing line at 6).
  for (let i = 0; i <= 5; i++) grid[i]![8] = get(i);
  grid[7]![8] = get(6);
  grid[8]![8] = get(7);
  grid[8]![7] = get(8);
  for (let i = 9; i < 15; i++) grid[8]![14 - i] = get(i);
  // Second copy, split across the top-right and bottom-left finders.
  for (let i = 0; i < 8; i++) grid[8]![size - 1 - i] = get(i);
  for (let i = 8; i < 15; i++) grid[size - 15 + i]![8] = get(i);
  grid[size - 8]![8] = true; // dark module

  if (ver >= 7) {
    // Version information (BCH(18,6)).
    let vrem = ver;
    for (let i = 0; i < 12; i++) vrem = (vrem << 1) ^ ((vrem >>> 11) * 0x1f25);
    const vbits = (ver << 12) | vrem;
    for (let i = 0; i < 18; i++) {
      const b = ((vbits >>> i) & 1) !== 0;
      const a = size - 11 + (i % 3);
      const c = Math.floor(i / 3);
      grid[a]![c] = b;
      grid[c]![a] = b;
    }
  }
}

function penalty(grid: Grid): number {
  const size = grid.length;
  let score = 0;
  // Rule 1: runs of 5+ same-color in rows and columns.
  for (let y = 0; y < size; y++) {
    let runColor = grid[y]![0]!;
    let runLen = 1;
    for (let x = 1; x < size; x++) {
      if (grid[y]![x] === runColor) {
        runLen++;
        if (runLen === 5) score += 3;
        else if (runLen > 5) score++;
      } else {
        runColor = grid[y]![x]!;
        runLen = 1;
      }
    }
  }
  for (let x = 0; x < size; x++) {
    let runColor = grid[0]![x]!;
    let runLen = 1;
    for (let y = 1; y < size; y++) {
      if (grid[y]![x] === runColor) {
        runLen++;
        if (runLen === 5) score += 3;
        else if (runLen > 5) score++;
      } else {
        runColor = grid[y]![x]!;
        runLen = 1;
      }
    }
  }
  // Rule 2: 2x2 blocks of same color.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const c = grid[y]![x]!;
      if (c === grid[y]![x + 1] && c === grid[y + 1]![x] && c === grid[y + 1]![x + 1]) score += 3;
    }
  }
  // Rules 3 & 4 are omitted: for a fixed short URL the mask chosen by rules 1+2
  // already scans reliably; skipping them keeps the encoder compact.
  return score;
}

/** Encode `text` into a QR module matrix (true = dark). */
export function encodeQr(text: string): Grid {
  const bytes = new TextEncoder().encode(text);
  const ver = pickVersion(bytes.length);
  const size = ver * 4 + 17;
  const dataCodewords = encodeData(text, ver);
  const allCodewords = interleave(dataCodewords, ver);

  // Try all 8 masks, keep the lowest-penalty result.
  let best: Grid | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const grid = makeGrid(size);
    const fn = makeGrid(size);
    drawFunctionPatterns(grid, fn, ver);
    drawCodewords(grid, fn, allCodewords);
    applyMask(grid, fn, mask);
    drawFormatBits(grid, ver, mask);
    const score = penalty(grid);
    if (score < bestScore) {
      bestScore = score;
      best = grid;
    }
  }
  return best!;
}

/** Render a module matrix as a self-contained, crisp SVG string. */
export function qrToSvg(matrix: Grid, quietZone = 4): string {
  const size = matrix.length;
  const dim = size + quietZone * 2;
  let path = '';
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (matrix[y]![x]) path += `M${x + quietZone},${y + quietZone}h1v1h-1z`;
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" ` +
    `shape-rendering="crispEdges" role="img" aria-label="Pairing QR code">` +
    `<rect width="${dim}" height="${dim}" fill="#fff"/>` +
    `<path d="${path}" fill="#000"/></svg>`
  );
}
